import React, { useMemo, useState, useContext } from "react";
import {
  useTheme,
  Typography,
  Box,
  TextField,
  Avatar,
  Stack,
  List,
  Divider,
  Popper,
  Paper,
  ClickAwayListener,
  Button,
  ListItemIcon,
  ListItemText,
  ListItem,
  ListItemButton,
} from "@mui/material";
import { orderCompaniesAlphabetically } from "../../features";
import PlaceIcon from "@mui/icons-material/Place";
import { AppContext } from "../../contextProviders/AppContext";
import { translation } from "../../components/Dictionary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TrainIcon from "@mui/icons-material/Train";
import LocalAirportIcon from "@mui/icons-material/LocalAirport";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { addDays, format, startOfWeek } from "date-fns";
import { fi } from "date-fns/locale";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";

const FIXED_GROUPS = window.URENT_APP_GROUPS;
const FEATURE_SELECT_CUSTOM_LABEL = FIXED_GROUPS?.includes("rsteam")
  ? true
  : false;

export default function RentalServiceSelector({
  providerSelectOpen,
  setProviderSelectOpen,
  contentHeigth,
  selectedPicker,
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);

  const {
    companies,
    handleSelectedCompany,
    selectedCompany,
    language,
    mobileViewPort,
    setOffers,
    enableCustomProviderLabel,
    setSelectedDateRange,
    setSelectedCompany,
  } = useContext(AppContext);

  const FEATURE_HIDE_SELECTED_COMPANY_TITLE = useMemo(() => {
    if (
      selectedCompany?.group === "gnista" ||
      selectedCompany?.group === "ms_vuokraus"
    ) {
      return true;
    }
  }, [selectedCompany]);

  const provideSelectorLabel = useMemo(() => {
    if (enableCustomProviderLabel()) {
      return enableCustomProviderLabel();
    }
    return translation.get(language, "Provider default title");
  }, [language]);

  const [open, setOpen] = useState(false);

  const orderedCompanies = useMemo(() => {
    if (!companies) return [];
    if (orderCompaniesAlphabetically(companies[0])) {
      return [...companies].sort((c1, c2) => {
        if (c1.displayName < c2.displayName) {
          return -1;
        }
        if (c1.displayName > c2.displayName) {
          return 1;
        }
        return 0; // Names are equal
      });
    }
    //Only sorts if both of our c1 & c2 orderNumbers are null or numbers (null - null = 0, so the order doesn't change)
    return [...companies].sort((c1, c2) => {
      return (
        (c1.orderNumber === null) - (c2.orderNumber === null) ||
        c1.orderNumber - c2.orderNumber
      );
    });
  }, [companies]);

  const serviceType = "legacy"; //"legacy" or "organization"
  const chooseCompanyLabel = useMemo(() => {
    if (enableCustomProviderLabel())
      return translation.get(language, "Select product group");
    return translation.get(language, "Select company label");
  }, [language]);

  const openMenu = (e) => {
    setProviderSelectOpen(!providerSelectOpen);
    setOpen((prevState) => !prevState);
    setAnchorEl(e.currentTarget);
  };

  const handleClickMenuItem = (e) => {
    if (serviceType === "legacy") {
      setAnchorEl(null);
      handleSelectedCompany(e);
      setOffers([]);
      setOpen(false);
      setProviderSelectOpen(false);
      setSelectedDateRange([null, null]);
    }
    if (serviceType === "organization") {
      setOffers([]);

      setProviderSelectOpen(false);
    }
  };
  const [searchInput, setSearchInput] = useState("");

  const searchResultsPickup = useMemo(() => {
    const result = orderedCompanies.filter((company) => {
      const name = company?.displayName ?? company?.name;
      if (name.toLowerCase().includes(searchInput.toLowerCase())) {
        return company;
      }
      return null;
    });

    if (result) {
      return result;
    }
    if (!searchInput) {
      return [];
    }
  }, [orderedCompanies, searchInput]);

  const styles = {
    height: contentHeigth,
    boxShadow: providerSelectOpen ? theme.shadows[2] : "transparent",
    /*   bgcolor: mobileViewPort
    ? theme.palette.primary.dialogBackground
    : undefined, */
    border: undefined,
    borderRadius: undefined,
    cursor: "pointer",
    borderTopLeftRadius: "45px",
    borderBottomLeftRadius: " 45px",
    borderTopRightRadius: "10px",
    borderBottomRightRadius: "10px",
    transition: "250ms ease-in-out",
    backgroundColor: Boolean(anchorEl) ? "white" : "transparent",
    "&:hover": {
      bgcolor: "rgba(0,0,0,0.05)",
      boxShadow: theme.shadows[2],
    },
    marginLeft: "2px",
  };

  const mobileStyles = {
    width: "100%",
    boxShadow: providerSelectOpen ? theme.shadows[2] : "transparent",
    /*   bgcolor: mobileViewPort
          ? theme.palette.primary.dialogBackground
          : undefined, */
    border: "1px solid lightgrey",
    borderRadius: "15px",
    cursor: "pointer",
    transition: "150ms ease-in-out",

    backgroundColor: "white",
    "&:hover": {
      bgcolor: "rgba(0,0,0,0.05)",
    },
  };

  const [showWeeklyBusinessHours, setShowWeeklyBusinessHours] = useState(null);

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
        setAnchorEl(null);
        setProviderSelectOpen(false);
      }}
    >
      <Box sx={mobileViewPort ? mobileStyles : styles}>
        <Stack
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            height: contentHeigth,
            borderTopLeftRadius: "45px",
            borderBottomLeftRadius: "45px",
          }}
          onClick={(e) => openMenu(e)}
        >
          {providerSelectOpen && (
            <Stack
              sx={{
                paddingLeft: "15px",
                flexDirection: "column",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: theme.palette.primary.mainText,
                  }}
                  noWrap
                >
                  {chooseCompanyLabel}
                </Typography>
              </Box>
              {selectedCompany && (
                <Box>
                  <Typography
                    sx={{
                      color: theme.palette.primary.mainText,
                    }}
                  >
                    {selectedCompany.displayName}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}

          {selectedCompany && !providerSelectOpen && (
            <Stack
              sx={{
                display: "flex",
                flexDirection: "column",
                paddingLeft: "10px",
                width: "100%",
              }}
            >
              {!FEATURE_HIDE_SELECTED_COMPANY_TITLE && (
                <>
                  <Typography
                    sx={{
                      color: theme.palette.primary.mainText,
                      fontSize: "16px",
                      fontFamily: "Sofia Pro",
                      fontWeight: 500,
                    }}
                    noWrap
                  >
                    {translation.get(language, "Selected company")}
                  </Typography>
                </>
              )}

              <Box width={"95%"}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.mainText,
                  }}
                  noWrap
                >
                  {selectedCompany.displayName}
                </Typography>
              </Box>
            </Stack>
          )}
          {!selectedCompany && !providerSelectOpen && (
            <Box
              sx={{
                marginLeft: "15px",
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  color: theme.palette.primary.mainText,
                  fontSize: "16px",
                  fontFamily: "Sofia Pro",
                }}
                noWrap
              >
                {provideSelectorLabel}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.primary.mainText,
                }}
                noWrap
              >
                {companies && chooseCompanyLabel}
                {!companies && <span>Ladataan toimipisteitä...</span>}
              </Typography>
            </Box>
          )}
          <Box sx={{ paddingX: "10px" }}>
            <ExpandMoreIcon sx={{ color: "lightgrey" }} />
          </Box>
        </Stack>

        <Popper
          onClose={() => {
            setOpen(false);
            setAnchorEl(null);
            setProviderSelectOpen(false);
          }}
          open={open && !selectedPicker}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 999 }}
        >
          <Paper
            sx={{
              borderRadius: "14px",
              padding: "20px",
            }}
          >
            <Stack direction={mobileViewPort ? "column" : "row"}>
              <Box sx={{ width: "300px" }}>
                {!selectedCompany && (
                  <>
                    <TextField
                      autoFocus
                      key={"searchInputField"}
                      fullWidth
                      id="searchInputField"
                      variant="outlined"
                      value={searchInput}
                      // focused={true}
                      //    inputRef={(input) => input && input.focus()}

                      onChange={(e) => setSearchInput(e.target.value)}
                      //   InputProps={{ disableUnderline: true }}
                      label="Etsi"
                    />
                    <Box
                      sx={{
                        overflowY: "auto",
                        width: "inherit",
                        overflowX: "hidden",
                      }}
                    >
                      <List sx={{ maxHeight: "400px", position: "relative" }}>
                        {searchResultsPickup.map((company) => (
                          <CompanyMenuItem
                            key={company.id}
                            company={company}
                            handleClickMenuItem={handleClickMenuItem}
                            setShowWeeklyBusinessHours={
                              setShowWeeklyBusinessHours
                            }
                          />
                        ))}
                      </List>
                    </Box>
                  </>
                )}
                {selectedCompany && (
                  <Stack direction="column" spacing={2}>
                    <Button
                      onClick={() => {
                        setSelectedCompany(null);
                        setShowWeeklyBusinessHours(null);
                      }}
                      variant="outlined"
                    >
                      Vaihda Toimipiste
                    </Button>

                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.primary.mainText,
                        textTransform: "uppercase",
                      }}
                    >
                      {selectedCompany.displayName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.primary.mainText }}
                    >
                      {selectedCompany.address}
                    </Typography>
                    <Divider />
                    <WeeklyBusinessHours company={selectedCompany} />
                  </Stack>
                )}
              </Box>

              {showWeeklyBusinessHours && !selectedCompany && (
                <Stack
                  direction={"column"}
                  sx={{ width: "280px", padding: "20px" }}
                  spacing={1}
                >
                  <Typography variant="body2" textTransform={"uppercase"}>
                    {showWeeklyBusinessHours.displayName ??
                      showWeeklyBusinessHours.name}
                  </Typography>
                  <Typography variant="body1">
                    {showWeeklyBusinessHours?.address}
                  </Typography>
                  <Stack direction={"row"}>
                    {showWeeklyBusinessHours?.postalCode && (
                      <Typography variant="body1" sx={{ marginRight: "5px" }}>
                        {showWeeklyBusinessHours?.postalCode}
                      </Typography>
                    )}
                    <Typography variant="body1">
                      {showWeeklyBusinessHours?.city}
                    </Typography>
                  </Stack>
                  {/*      <Stack direction={"row"}>
                    <LocalPhoneIcon />
                    <Typography>{showWeeklyBusinessHours?.phone}</Typography>
                  </Stack> */}

                  <Divider />
                  <WeeklyBusinessHours company={showWeeklyBusinessHours} />
                </Stack>
              )}
            </Stack>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

function CompanyMenuItem({
  company,
  handleClickMenuItem,
  setShowWeeklyBusinessHours,
}) {
  return (
    <ListItem onMouseEnter={() => setShowWeeklyBusinessHours(company)}>
      <ListItemButton onClick={() => handleClickMenuItem(company)}>
        <ListItemIcon>
          <Icon company={company} />
        </ListItemIcon>
        <ListItemText
          primary={company?.displayName ?? company?.name}
          secondary={company?.address}
          primaryTypographyProps={{ fontWeight: 550 }}
          secondaryTypographyProps={{
            fontFamily: "Roboto",
            color: "grey",
            whiteSpace: "nowrap",
            noWrap: true,
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

function WeeklyBusinessHours({ company }) {
  const weeklyBusinessHours = [...company.weeklyBusinessHours]; // Create a copy of the array

  const firstItem = weeklyBusinessHours.shift(); // Remove the first item from the array
  weeklyBusinessHours.push(firstItem); // Add the first item to the last position
  if (weeklyBusinessHours?.length > 0) {
  }

  const alternativeSubText = "Peräkärryjen vuokrauksen aukioloajat"; // This is a special case for a specific organization: Tokmanni
  const defaultSubText =
    " Nämä aukioloajat voivat vaihdella julkisten juhlapyhien vuoksi, ja niihin voi tulla muutoksia.";

  const text =
    company?.organizationId &&
    company?.organizationId === "9d099f0f-486c-4c12-a6e2-f8ac7f006387"
      ? alternativeSubText
      : defaultSubText;
  return (
    <Stack direction={"column"} spacing={2} sx={{ maxWidth: "300px" }}>
      {weeklyBusinessHours.map((day, index) => {
        if (day) {
          return (
            <Stack
              direction={"row"}
              width={"100%"}
              alignItems={"center"}
              key={`weekly-business-hours-${index}`}
            >
              <Weekday index={index} />
              <Typography>
                {day?.openAt === "00:00" && day?.closeAt === "23:59"
                  ? "Avoinna ympäri vuorokauden"
                  : `${day?.openAt} - ${day?.closeAt}`}
              </Typography>
            </Stack>
          );
        } else {
          return (
            <Stack
              direction={"row"}
              width={"100%"}
              key={`weekly-business-hours-${index}`}
            >
              <Weekday index={index} />
              <Typography sx={{ color: "darkgrey" }}> Suljettu</Typography>
            </Stack>
          );
        }
      })}
      <Typography variant="caption" sx={{ color: "darkgrey" }}>
        {text}
      </Typography>
    </Stack>
  );
}

function Weekday({ index }) {
  return (
    <Box width={"35px"}>
      <Typography
        sx={{
          fontWeight: 550,
          textTransform: "uppercase",
          marginRight: "5px",
        }}
      >
        {getWeekdayNameByIndex(index)}:
      </Typography>
    </Box>
  );
}

function Icon({ company }) {
  return (
    <Avatar variant="rounded" sx={{ marginRight: "10px" }}>
      <LocationIcon company={company} />
    </Avatar>
  );
}

function LocationIcon({ company }) {
  const iconColor = "rgba(0,0,0,0.55)";
  if (company?.locationType === "Airport") {
    return <LocalAirportIcon sx={{ color: iconColor }} />;
  }
  if (company?.locationType === "Railway Station") {
    return <TrainIcon sx={{ color: iconColor }} />;
  }

  if (company?.locationType === "City") {
    return <LocationCityIcon sx={{ color: iconColor }} />;
  }

  return <PlaceIcon sx={{ color: iconColor }} />;
}

function getWeekdayNameByIndex(index) {
  // Assuming week starts on Sunday and index 0 corresponds to Sunday
  const baseDate = startOfWeek(new Date(), { weekStartsOn: 1 });
  const targetDate = addDays(baseDate, index);
  return format(targetDate, "EEEEEE", { locale: fi });
}
